import type React from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useEffect } from 'react';

import './Slider.css';

import type { ColorName } from 'styles/config/colors';

import { cn } from 'utils/cn';
import { colorCssVar } from 'utils/colorCssVar';
import Input from '../Input';
import InputWrapper from '../InputWrapper';
import { BodyStrong } from '../LegacyText';

export const THUMB_WIDTH = 24;

export type SliderTooltipWidth = 'auto' | 'fixed';
export type SliderTooltipPosition = 'left' | 'right' | 'center';

export interface SliderProps extends InputHTMLAttributes<HTMLInputElement> {
  minValue?: number;
  maxValue?: number;
  segments?: { label: string; value: string | number }[];
  backgroundColor?: ColorName;
  thumbBackgroundColor?: ColorName;
  onHoverThumbBackgroundColor?: ColorName;
  sliderWidth?: string;
  mobileWidth?: boolean;
  showValueInInput?: boolean;
  disabled?: boolean;
  label?: ReactNode;
  help?: string;
  knobTooltipOverride?: string | false;
  hideKnobTooltip?: boolean;
  sliderTooltipPosition?: SliderTooltipPosition;
  minValueLabel?: (value: number) => string | null | undefined;
  maxValueLabel?: (value: number) => string | null | undefined;
  knobTooltip?: (value: number) => string | null | undefined;
  formatInputValue?: (value: number) => string;
  'data-test-id-tooltip'?: string;
  errors?: string[];
}

const calculateOffset = (value: number, minValue: number, maxValue: number) => {
  return ((value - minValue) / (maxValue - minValue)) * 100;
};

const calculateTooltipPosition = (
  value: number,
  minValue: number,
  maxValue: number,
) => {
  const range = maxValue - minValue;
  const position = ((value - minValue) / range) * 100;
  const positionOffset =
    Math.round((THUMB_WIDTH * position) / 100) - THUMB_WIDTH / 2;
  return `calc(${position}% - ${positionOffset}px)`;
};

export const Slider = forwardRef(
  (
    {
      label,
      help,
      backgroundColor = 'sliderTrackActive',
      thumbBackgroundColor = 'sliderThumb',
      onHoverThumbBackgroundColor = 'sliderThumb',
      sliderWidth,
      mobileWidth = false,
      showValueInInput = false,
      disabled = false,
      onChange,
      segments,
      value: propsValue,
      defaultValue: propsDefaultValue,
      minValue: propsMinValue,
      maxValue: propsMaxValue,
      hideKnobTooltip = false,
      sliderTooltipPosition,
      minValueLabel: propsMinValueLabelFn,
      maxValueLabel: propsMaxValueLabelFn,
      knobTooltip: propsKnobTooltipFn,
      formatInputValue,
      'data-test-id-tooltip': tooltipTestId = 'tooltip-text',
      errors,
      ...restProps
    }: SliderProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const getVariables = () => {
      if (segments) {
        const defaultValueIndex = segments.findIndex(
          segment => propsDefaultValue === segment.value,
        );
        const valueIndex = segments.findIndex(
          segment => propsValue === segment.value,
        );
        return {
          range: [0, segments.length - 1],
          defaultValue: defaultValueIndex,
          value: valueIndex,
          knobTooltip: segments[valueIndex]?.label,
          minValueLabel: segments[0].label,
          maxValueLabel: segments[segments.length - 1].label,
        };
      }
      const minValue = propsMinValue || 0;
      const maxValue = propsMaxValue || 0;
      const currentValue = propsValue as number;
      return {
        range: [minValue, maxValue],
        defaultValue: propsDefaultValue as number,
        value: currentValue,
        knobTooltip: propsKnobTooltipFn
          ? propsKnobTooltipFn(currentValue)
          : currentValue,
        minValueLabel: propsMinValueLabelFn
          ? propsMinValueLabelFn(minValue)
          : minValue,
        maxValueLabel: propsMaxValueLabelFn
          ? propsMaxValueLabelFn(maxValue)
          : maxValue,
      };
    };

    const {
      range: [minValue, maxValue],
      defaultValue,
      value,
      knobTooltip,
      minValueLabel,
      maxValueLabel,
    } = getVariables();

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      if (inputValue === '') {
        e.target.value = String(value || '');
        return;
      }

      const numericValue = Number(inputValue);

      // Validate the input value
      if (
        !isNaN(numericValue) &&
        numericValue >= minValue &&
        numericValue <= maxValue
      ) {
        onChange?.(e as any);
      } else {
        e.target.value = String(value || '');
      }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        (e.target as HTMLInputElement).blur();
      }
    };

    useEffect(() => {
      if (
        process.env.NODE_ENV !== 'production' &&
        !segments &&
        (minValue === undefined || maxValue === undefined)
      ) {
        console.warn('[Slider] minValue and maxValue must be provided');
      }
    }, [segments, minValue, maxValue]);

    const fillPercent = calculateOffset(value, minValue, maxValue);
    const trackActiveColor = colorCssVar(backgroundColor);
    const trackInactiveColor = colorCssVar('sliderTrackInactive' as ColorName);

    return (
      <InputWrapper
        data-test-id-tooltip={tooltipTestId}
        errors={errors}
        help={help}
        label={label}
      >
        {/* SliderRow */}
        <div
          className={cn(
            'flex flex-row gap-xs3 items-center',
            'w-full',
            disabled && 'text-disabled',
            '[&>.minLabel]:flex-shrink-0 [&>.minLabel]:min-w-sm4 [&>.minLabel]:text-center',
            '[&>.slider]:flex-1',
            '[&>.maxLabel]:flex-shrink-0 [&>.maxLabel]:min-w-sm4 [&>.maxLabel]:text-center',
            '[&>.inputField]:flex-shrink-0 [&>.inputField]:ml-xs3',
          )}
        >
          <BodyStrong className="minLabel">{minValueLabel}</BodyStrong>
          {/* RangeControl */}
          <div
            className={cn(
              'relative flex items-center slider',
              mobileWidth && 'w-full',
            )}
          >
            {segments && (
              /* Segments */
              <span className="pointer-events-none absolute flex gap-xs1 w-full h-full">
                {Array.from({ length: maxValue - minValue }).map((_, index) => (
                  <span
                    // biome-ignore lint/suspicious/noArrayIndexKey: existing
                    key={index}
                    className={cn(
                      'w-full h-full rounded-xSmall',
                      disabled
                        ? value > index
                          ? 'bg-background-disabled-strong'
                          : 'bg-background-disabled-subtle'
                        : value > index
                          ? 'bg-[var(--tw-slider-track-active)]'
                          : 'bg-[var(--tw-slider-track-inactive)]',
                    )}
                  />
                ))}
              </span>
            )}
            <input
              ref={ref}
              className={cn(
                'w-full m-0 appearance-none rounded-large',
                disabled && 'pointer-events-none',
              )}
              defaultValue={defaultValue}
              disabled={disabled}
              max={maxValue}
              min={minValue}
              type="range"
              value={value}
              onChange={onChange}
              style={{
                height: '4px',
                background: segments
                  ? 'transparent'
                  : `linear-gradient(to right, ${trackActiveColor} ${fillPercent}%, ${trackInactiveColor} 0)`,
                // Custom thumb styles via CSS custom properties exposed as inline vars
                // The actual thumb styling needs a global CSS rule; we use data-* for hooks
              }}
              {...restProps}
            />
            {!hideKnobTooltip && (
              /* SliderTooltip */
              <output
                className="absolute pointer-events-none hidden"
                name="rangeVal"
                style={{
                  top: `-40px`,
                  left: calculateTooltipPosition(value, minValue, maxValue),
                  transform:
                    sliderTooltipPosition === 'left'
                      ? 'translateX(-90%)'
                      : sliderTooltipPosition === 'right'
                        ? 'translateX(-10%)'
                        : 'translateX(-50%)',
                  width: segments ? 'auto' : '40px',
                  height: 'auto',
                  fontSize: '12px',
                  lineHeight: segments ? '1.2' : '2',
                  color: 'var(--tw-text-primary-inverse)',
                  textAlign: 'center',
                  backgroundColor: 'var(--tw-background-inverse)',
                  borderRadius: '4px',
                  padding: segments ? '4px 12px' : undefined,
                }}
              >
                {knobTooltip}
              </output>
            )}
          </div>
          <BodyStrong className="maxLabel">{maxValueLabel}</BodyStrong>
          {showValueInInput && !segments && (
            <div className="inputField">
              <Input
                key={`slider-${value}`}
                className="w-[54px] h-[40px] !p-xs2 !font-regular text-center"
                defaultValue={
                  formatInputValue && value != null
                    ? formatInputValue(value as number)
                    : String(value ?? '')
                }
                disabled={disabled}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
              />
            </div>
          )}
        </div>
      </InputWrapper>
    );
  },
);
